import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { serviceCategoriesService, serviceProviderApplicationsService } from '../services/api';

interface ApplicationFormData {
  business_name: string;
  business_registration_number: string;
  service_categories: string[];
  experience_years: number;
  description: string;
  certifications: string[];
  portfolio_links: string[];
  hourly_rate_min: number;
  hourly_rate_max: number;
  availability: string[];
  service_areas: string[];
  emergency_contact_name: string;
  emergency_contact_phone: string;
}

const ApplyServiceProviderPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ApplicationFormData>({
    business_name: '',
    business_registration_number: '',
    service_categories: [],
    experience_years: 0,
    description: '',
    certifications: [],
    portfolio_links: [],
    hourly_rate_min: 0,
    hourly_rate_max: 0,
    availability: [],
    service_areas: [],
    emergency_contact_name: '',
    emergency_contact_phone: ''
  });
  const [documents, setDocuments] = useState<File[]>([]);
  const [existingApplication, setExistingApplication] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [serviceCategories, setServiceCategories] = useState<string[]>([]);

  const fallbackServiceCategories = [
    'cleaning', 'repair', 'delivery', 'tutoring', 'design', 
    'writing', 'programming', 'consulting', 'fitness', 'beauty',
    'photography', 'videography', 'translation', 'legal', 'accounting',
    'marketing', 'event-planning', 'catering', 'landscaping', 'moving', 'house-hunting', 'other'
  ];

  const availabilityOptions = [
    'Monday Morning', 'Monday Afternoon', 'Monday Evening',
    'Tuesday Morning', 'Tuesday Afternoon', 'Tuesday Evening',
    'Wednesday Morning', 'Wednesday Afternoon', 'Wednesday Evening',
    'Thursday Morning', 'Thursday Afternoon', 'Thursday Evening',
    'Friday Morning', 'Friday Afternoon', 'Friday Evening',
    'Saturday Morning', 'Saturday Afternoon', 'Saturday Evening',
    'Sunday Morning', 'Sunday Afternoon', 'Sunday Evening',
    'Weekends Only', 'Weekdays Only', 'Flexible Hours', '24/7 Available'
  ];

  useEffect(() => {
    loadExistingApplication();
    loadServiceCategories();
  }, []);

  const loadServiceCategories = async () => {
    try {
      const response = await serviceCategoriesService.getCategories(false);
      const categories = (response.data || []).map((category: { name: string }) => category.name);

      if (categories.length > 0) {
        setServiceCategories(categories);
        return;
      }

      setServiceCategories(fallbackServiceCategories);
    } catch (loadError) {
      console.error('Failed to load service categories:', loadError);
      setServiceCategories(fallbackServiceCategories);
    }
  };

  const loadExistingApplication = async () => {
    try {
      const response = await serviceProviderApplicationsService.getMyApplication();
      if (response.data) {
        setExistingApplication(response.data);
        setFormData({
          business_name: response.data.business_name || '',
          business_registration_number: response.data.business_registration_number || '',
          service_categories: response.data.service_categories || [],
          experience_years: response.data.experience_years || 0,
          description: response.data.description || '',
          certifications: response.data.certifications || [],
          portfolio_links: response.data.portfolio_links || [],
          hourly_rate_min: response.data.hourly_rate_min || 0,
          hourly_rate_max: response.data.hourly_rate_max || 0,
          availability: response.data.availability || [],
          service_areas: response.data.service_areas || [],
          emergency_contact_name: response.data.emergency_contact_name || '',
          emergency_contact_phone: response.data.emergency_contact_phone || ''
        });
      }
    } catch (error) {
      console.log('No existing application found');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  const handleArrayChange = (field: keyof ApplicationFormData, value: string) => {
    setFormData(prev => {
      const currentArray = prev[field] as string[];
      const newArray = currentArray.indexOf(value) !== -1
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];
      return {
        ...prev,
        [field]: newArray
      };
    });
  };

  const handleAddToArray = (field: keyof ApplicationFormData, value: string) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...(prev[field] as string[]), value.trim()]
      }));
    }
  };

  const handleRemoveFromArray = (field: keyof ApplicationFormData, index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] as string[]).filter((_, i) => i !== index)
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setDocuments(Array.from(e.target.files));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const submitData = new FormData();
      
      // Add form data
      for (const key in formData) {
        if (!Object.prototype.hasOwnProperty.call(formData, key)) {
          continue;
        }

        const value = formData[key as keyof ApplicationFormData];
        if (Array.isArray(value)) {
          submitData.append(key, JSON.stringify(value));
        } else {
          submitData.append(key, value.toString());
        }
      }

      // Add documents
      documents.forEach(file => {
        submitData.append('documents', file);
      });

      let response;
      if (existingApplication && existingApplication.application_status === 'pending') {
        response = await serviceProviderApplicationsService.updateApplication(existingApplication.id, submitData);
        setSuccess('Application updated successfully!');
      } else {
        response = await serviceProviderApplicationsService.createApplication(submitData);
        setSuccess('Application submitted successfully!');
      }

      setTimeout(() => {
        navigate('/apply-service-provider/status');
      }, 2000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  if (existingApplication && existingApplication.application_status === 'approved') {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow p-6">
          <div className="text-center">
            <div className="text-green-500 text-5xl mb-4">✅</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Approved</h2>
            <p className="text-gray-600 mb-4">
              Your service provider application has been approved! You can now start bidding on services.
            </p>
            <button
              onClick={() => navigate('/services')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
            >
              Go to Services
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (existingApplication && existingApplication.application_status === 'rejected') {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow p-6">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Rejected</h2>
            <p className="text-gray-600 mb-4">
              Unfortunately, your service provider application was rejected.
            </p>
            {existingApplication.admin_comments && (
              <div className="bg-gray-50 p-3 rounded text-left mb-4">
                <p className="text-sm font-medium text-gray-700">Admin Comments:</p>
                <p className="text-sm text-gray-600">{existingApplication.admin_comments}</p>
              </div>
            )}
            <p className="text-sm text-gray-500 mb-4">
              You can submit a new application with updated information.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
            >
              Submit New Application
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {existingApplication ? 'Update Service Provider Application' : 'Apply to Become a Service Provider'}
            </h1>
            <p className="text-gray-600 mb-8">
              Fill out this form to apply as a service provider. Once approved, you'll be able to bid on service requests.
            </p>

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="text-red-400">⚠️</div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <div className="mt-2 text-sm text-red-700">{error}</div>
                  </div>
                </div>
              </div>
            )}

            {success && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
                <div className="flex">
                  <div className="text-green-400">✅</div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">Success</h3>
                    <div className="mt-2 text-sm text-green-700">{success}</div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Business Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Business Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Business Name</label>
                    <input
                      type="text"
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Your business or professional name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Registration Number (Optional)</label>
                    <input
                      type="text"
                      name="business_registration_number"
                      value={formData.business_registration_number}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Business registration number"
                    />
                  </div>
                </div>
              </div>

              {/* Service Categories */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Service Categories</h3>
                <p className="text-sm text-gray-600 mb-3">Select the categories of services you can provide:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {serviceCategories.map((category) => (
                    <label key={category} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.service_categories.indexOf(category) !== -1}
                        onChange={() => handleArrayChange('service_categories', category)}
                        className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 capitalize">{category.replace('-', ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Experience and Description */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Years of Experience</label>
                  <input
                    type="number"
                    name="experience_years"
                    value={formData.experience_years}
                    onChange={handleNumberChange}
                    min="0"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700">Hourly Rate Range (KSH)</label>
                  <div className="grid grid-cols-2 gap-3 mt-1">
                    <input
                      type="number"
                      name="hourly_rate_min"
                      value={formData.hourly_rate_min}
                      onChange={handleNumberChange}
                      min="0"
                      placeholder="Min"
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <input
                      type="number"
                      name="hourly_rate_max"
                      value={formData.hourly_rate_max}
                      onChange={handleNumberChange}
                      min="0"
                      placeholder="Max"
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe your services, experience, and what makes you unique..."
                />
              </div>

              {/* Availability */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Availability</h3>
                <p className="text-sm text-gray-600 mb-3">Select when you're available to provide services:</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {availabilityOptions.map((time) => (
                    <label key={time} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.availability.indexOf(time) !== -1}
                        onChange={() => handleArrayChange('availability', time)}
                        className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{time}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Emergency Contact */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Emergency Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Name</label>
                    <input
                      type="text"
                      name="emergency_contact_name"
                      value={formData.emergency_contact_name}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
                    <input
                      type="tel"
                      name="emergency_contact_phone"
                      value={formData.emergency_contact_phone}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Supporting Documents</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Upload Documents</label>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    className="mt-1 block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded file:border-0
                      file:text-sm file:font-medium
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Upload relevant certificates, ID, portfolio samples, etc. (PDF, DOC, DOCX, JPG, PNG)
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/services')}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50"
                >
                  {loading ? 'Submitting...' : (existingApplication ? 'Update Application' : 'Submit Application')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplyServiceProviderPage;